/** @format */
import isPropValid from '@emotion/is-prop-valid';
import {
    Button,
    ButtonSize,
    ButtonVariant,
} from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { animated, useTransition } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import styled from 'styled-components';

import { SearchFacet } from '@toucan/common/services/search/constants';

import Cross from '@toucan/client/global/icons/legacy/cross.svg';

import {
    IconButton,
    IconButtonFill,
    IconButtonSize,
} from '@toucan/client/shared/components/icon-button';
import { Link, LinkSize } from '@toucan/client/shared/components/link';

import {
    Accordion,
    AccordionSection,
} from '@toucan/client/blocks/components/accordion';

import {
    Filter,
    FilterOption,
} from '@toucan/client/features/search/config/types';

import { AccessoryFilters } from '../accessory-filters';
import { ConditionFilter } from '../condition-filter';
import { PriceFilter } from '../price-filter';
import { useResultsAvailable } from '../search-filter-menu/hooks/use-results-available';
import { SectionTitleChildNode } from './components/section-title-child-node';
import { TEST_ID } from './constants';
import { styles } from './search-filter-modal.styles';
import { SearchFilterModalProps } from './types';

const StyledSearchFilterModal = styled(animated.aside).withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const SearchFilterModal: FunctionComponent<SearchFilterModalProps> = ({
    isOpen,
    filters,
    resultsCount,
    onApply,
    onCancel,
    onClose,
    baseQuery,
    totalInitialResults,
}: SearchFilterModalProps) => {
    const { t } = useTranslation();

    const [activeAccordionSection, setActiveAccordionSection] = useState<
        string | null
    >(null);

    const [selectedFilters, setSelectedFilters] = useState<Filter[]>([]);

    const transitions = useTransition(isOpen, {
        from: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
        enter: {
            transform: 'translateY(0)',
            opacity: 1,
        },
        leave: {
            transform: 'translateY(5rem)',
            opacity: 0,
        },
        onRest: () => {
            if (!isOpen) {
                setActiveAccordionSection(null);
            }
        },
    });

    const { data: resultsAvailable, isFetching: isResultsAvailableFetching } =
        useResultsAvailable({
            baseQuery,
            filters: selectedFilters,
        });

    const searchButtonText = useMemo(() => {
        if (resultsAvailable === 0) {
            return t('No results');
        }
        return t('Show {{count}} products', { count: resultsAvailable });
    }, [resultsAvailable, t]);

    const handleFilterChange = useCallback(
        (filter: Filter, value: FilterOption[]) => {
            setSelectedFilters((prev) => {
                return prev.map((prevFilter) => {
                    if (prevFilter.id === filter.id) {
                        return { ...prevFilter, selectedOptions: value };
                    }
                    return prevFilter;
                });
            });
        },
        []
    );

    const handleApplyFilters = useCallback(() => {
        onApply(selectedFilters);
    }, [onApply, selectedFilters]);

    function clearChangesToFilterGroups() {
        setSelectedFilters(filters);
    }

    function removeSelectedFilterOptionsInFilterGroup(id: Filter['id']) {
        const filters = selectedFilters.map((filter) => {
            if (filter.id === id) {
                return { ...filter, selectedOptions: [] };
            }
            return filter;
        });

        setSelectedFilters(filters);
    }

    function removeAllSelectedFilterOptions() {
        const filtersWithNoSelectedOptions = selectedFilters.map((filter) => ({
            ...filter,
            selectedOptions: [],
        }));

        setSelectedFilters(filtersWithNoSelectedOptions);
    }

    function renderFilterOptions(filter: Filter) {
        if (filter.id === SearchFacet.productConditionStarRating) {
            return (
                <ConditionFilter
                    key={filter.id}
                    filter={filter}
                    onConditionSelect={(value) => {
                        handleFilterChange(filter, value);
                    }}
                    showTitle={false}
                />
            );
        }
        if (filter.id === SearchFacet.productPrice) {
            return (
                <PriceFilter
                    key={filter.id}
                    filter={filter}
                    onPriceSelect={(value) => {
                        handleFilterChange(filter, value);
                    }}
                    showTitle={false}
                />
            );
        }

        if (filter.id === SearchFacet.productPriceModifier) {
            return (
                <AccessoryFilters
                    key={filter.id}
                    filter={filter}
                    onAccessorySelect={(value) => {
                        handleFilterChange(filter, value);
                    }}
                    totalInitialResults={totalInitialResults}
                    showIcons={false}
                    isGroupNameFullWidthStyle
                />
            );
        }
        return;
    }

    function getAccordionSections(): AccordionSection[] {
        return selectedFilters.map((filter) => {
            const { label, id } = filter;

            return {
                id,
                title: label,
                showTitleChildNode: true,
                titleChildNode: (
                    <SectionTitleChildNode
                        isOpen={activeAccordionSection === id}
                        selectedCount={filter.selectedOptions.length}
                        onClearAll={() => {
                            removeSelectedFilterOptionsInFilterGroup(id);
                        }}
                    />
                ),
                content: renderFilterOptions(filter),
                'data-testid': `filter-section-title--${id}`,
            };
        });
    }

    useEffect(() => {
        setSelectedFilters([...filters]);
    }, [filters]);

    return (
        <Fragment>
            {transitions((props, item) =>
                item ? (
                    <StyledSearchFilterModal
                        style={props}
                        data-testid={TEST_ID}
                    >
                        <div className="filter-content">
                            <div className="filter-header">
                                <Heading
                                    size={HeadingSize.H3}
                                    className="filter-header-title"
                                >
                                    {t('Filters')}
                                </Heading>

                                <IconButton
                                    label={t('Close')}
                                    fill={IconButtonFill.HOLLOW}
                                    border={false}
                                    size={IconButtonSize.MEDIUM}
                                    className="filter-header-close"
                                    onClick={() => {
                                        clearChangesToFilterGroups();
                                        onClose();
                                    }}
                                    data-testid="mobile-search-filter__close"
                                >
                                    <Cross />
                                </IconButton>
                                <p className="filter-header-results">
                                    <strong>{resultsCount}</strong>{' '}
                                    {t('results')}
                                </p>
                                {selectedFilters.length !== 0 && (
                                    <Link
                                        size={LinkSize.SMALL}
                                        className="filter-header-clear"
                                        data-testid="mobile-search-filter__clear-all"
                                        onClick={() => {
                                            removeAllSelectedFilterOptions();
                                        }}
                                    >
                                        {t('Clear all filters')}
                                    </Link>
                                )}
                            </div>

                            <Accordion
                                className="filter-accordion"
                                activeSection={activeAccordionSection}
                                sections={getAccordionSections()}
                                onActiveSectionChange={
                                    setActiveAccordionSection
                                }
                            />

                            <div className="filter-buttons">
                                <Button
                                    variant={ButtonVariant.TERTIARY}
                                    size={ButtonSize.SMALL}
                                    className="filter-button"
                                    onClick={() => {
                                        clearChangesToFilterGroups();
                                        onCancel();
                                    }}
                                    data-testid="mobile-search-filter__cancel"
                                >
                                    {t('Cancel')}
                                </Button>

                                <Button
                                    data-testid="mobile-search-filter__apply"
                                    isLoading={isResultsAvailableFetching}
                                    disabled={
                                        isResultsAvailableFetching ||
                                        resultsAvailable === 0
                                    }
                                    className="filter-button"
                                    size={ButtonSize.SMALL}
                                    onClick={handleApplyFilters}
                                >
                                    {searchButtonText}
                                </Button>
                            </div>
                        </div>
                    </StyledSearchFilterModal>
                ) : null
            )}
        </Fragment>
    );
};
